import { Autocomplete, Box, Button, Chip, OutlinedInput, Stack, TextField } from "@mui/material";
import React, { useState } from "react";
import { GridColDef, GridRowsProp, useGridApiRef } from "@mui/x-data-grid-premium";
import ListPageHeader, {
    ListPageHeaderGroupActions,
    ListPageHeaderRight,
} from "../../../../Components/Misc/ListPageHeader";
import TableSkeleton from "../../../../Components/Table/TableSkeleton";
import moment from "moment";
import GlobalConst from "../../../../Core/Utils/Constants/globals";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import { logService, userService } from "../../../../Core/Api/services";
import { DbUserBasicInfos } from "../../../../Core/Api/Types/db-user-basic-infos";
import SearchIcon from "@mui/icons-material/Search";
import { DbActivityInfos } from "../../../../Core/Api/Types/db-activity-infos";
import TableCell2Lines from "../../../../Components/Table/TableCell2Lines/table-cell-2-lines";
import EmptyListSlot from "../../../../Components/Misc/EmptyListSlot";
import TableCellBool from "../../../../Components/Table/TableCellBool";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useTranslation } from "react-i18next";
import TableUuids from "../../../../Components/Table/table-uuids";

function DashboardActivities() {
    const { t } = useTranslation();
    const [searchFilter, setSearchFilter] = React.useState<string>("");
    const [users, setUsers] = React.useState<DbUserBasicInfos[]>([]);
    const [userId, setUserId] = React.useState<number | undefined>(undefined);
    //const [dateIn, setDateIn] = React.useState<string>("");
    // const [dateOut, setDateOut] = React.useState<string>("");
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const apiGrid = useGridApiRef();

    // Table rows
    const [rows, setRows] = useState<DbActivityInfos[]>([]);

    const dayDepth = 15;

    React.useEffect(() => {
        userService.GetAllUsersBasic().then((response) => {
            if (response.users) setUsers(response.users);
        });
    }, []);

    const handleFetch = React.useCallback(() => {
        if (!userId) return;
        setIsLoading(true);

        let a = moment().add(-dayDepth, "d").format("YYYY-MM-DD");
        let b = moment().add(1, "d").format("YYYY-MM-DD");
        logService.ListAllUserActivitiesByPeriod(userId, a, b).then((response) => {
            if (response.activities) {
                setRows(response.activities);
                setIsLoading(false);
            }
        });
    }, [userId, dayDepth]);

    // Export XSL
    const exportExcel = () => {
        apiGrid.current.exportDataAsCsv({ delimiter: GlobalConst.EXPORT_DELIMITER, allColumns: true });
    };

    // #region Grid definition
    let _rows: GridRowsProp = [];
    type Row = (typeof _rows)[number];

    const columns: GridColDef[] = React.useMemo(
        () => [
            {
                field: "date",
                headerName: t("common:date"),
                flex: CellSize.small,
                minWidth: CellSize.small,
                renderCell: (data: Row) => moment(data.value).format(GlobalConst.MOMENT_DATE_FORMAT_FULL),
            },
            {
                field: "owner",
                headerName: t("common:owner"),
                flex: CellSize.small,
                minWidth: CellSize.small,
                valueGetter: (data: any, row: any) => row?.userId === userId,
                renderCell: (data: Row) => <TableCellBool value={data?.row?.userId === userId} />,
            },
            {
                field: "kind",
                headerName: t("common:kind"),
                flex: CellSize.large,
                minWidth: CellSize.large,
                renderCell: (data: Row) => <Chip label={data.value} color="secondary" size="small" sx={{ mt: 1, mb: 1 }} />,
            },
            {
                field: "typeExp",
                headerName: t("common:type"),
                flex: CellSize.medium,
                minWidth: CellSize.medium,
                renderCell: (data: Row) => <Chip label={data.value} color="default" size="small" />,
            },
            {
                field: "objName",
                headerName: t("common:object"),
                flex: CellSize.xlarge,
                minWidth: CellSize.xlarge,
                renderCell: (data: Row) => <TableCell2Lines title={data?.row?.msg} desc={data?.row?.objName} />,
            },
        ],
        [rows]
    );

    return (
        <>
            <ListPageHeader>
                <ListPageHeaderGroupActions>
                    <Autocomplete
                        value={users.find((x) => x.id === userId) || null}
                        onChange={(e, value) => setUserId(value?.id ?? -1)}
                        options={users}
                        getOptionLabel={(o: DbUserBasicInfos) => o.firstName + " " + o.lastName + " (" + o.login + ")"}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        sx={{ width: "100%", minWidth: 350 }}
                        id={"statusFilter"}
                        renderInput={(params) => (
                            <TextField placeholder={t("field:placeholder.selectUser")} sx={{ mt: "-24px" }} {...params} />
                        )}
                        renderOption={(props, option) => {
                            return (
                                <Box {...props} key={option.id} component="li">
                                    {option.firstName + " " + option.lastName + " (" + option.login + ")"}
                                </Box>
                            );
                        }}
                    />
                </ListPageHeaderGroupActions>
                <ListPageHeaderRight>
                    <Button variant="contained" onClick={handleFetch}>
                        {t("button:searchWithinLastDays", { count: dayDepth })}
                    </Button>
                </ListPageHeaderRight>
            </ListPageHeader>

            {!isLoading ? (
                rows.length > 0 ? (
                    <DataTable
                        tableKey={TableUuids.DASHBOARD_ACTIVITIES_TABLE}
                        rows={rows.filter(
                            (x) =>
                                searchFilter === "" ||
                                x.objName?.includesAllWords(searchFilter) ||
                                x.msg?.includesAllWords(searchFilter)
                        )}
                        columns={columns}
                        getRowId={(row) => row.id}
                        getRowHeight={() => "auto"}
                        columnHeaderHeight={48}
                        disableRowSelectionOnClick={true}
                        apiRef={apiGrid}
                    />
                ) : (
                    <EmptyListSlot
                        title={t("component:emptyListSlot.title.selectUserToSeeActivities")}
                        desc={t("component:emptyListSlot.desc.listUserActivities")}
                        icon={<SearchIcon />}
                    />
                )
            ) : (
                <TableSkeleton />
            )}
        </>
    );
}

export default DashboardActivities;
